import { collaborativeContentSnapshot } from './yjsTransactions';

const URL_ONLY = /^https?:\/\/[^\s]+$/i;
export const MAX_BRANCH_MARKDOWN_BYTES = 2 * 1024 * 1024;

function finite(value, fallback = 0) {
  return Number.isFinite(value) ? value : fallback;
}

function utf8ByteLength(value) {
  const text = String(value || '');
  let bytes = 0;
  for (let index = 0; index < text.length; index += 1) {
    const codeUnit = text.charCodeAt(index);
    if (codeUnit <= 0x7f) {
      bytes += 1;
    } else if (codeUnit <= 0x7ff) {
      bytes += 2;
    } else if (
      codeUnit >= 0xd800
      && codeUnit <= 0xdbff
      && index + 1 < text.length
      && text.charCodeAt(index + 1) >= 0xdc00
      && text.charCodeAt(index + 1) <= 0xdfff
    ) {
      bytes += 4;
      index += 1;
    } else {
      bytes += 3;
    }
  }
  return bytes;
}

function compareNodes(left, right) {
  const leftOrder = finite(left?.layout?.order, Number.POSITIVE_INFINITY);
  const rightOrder = finite(right?.layout?.order, Number.POSITIVE_INFINITY);
  if (leftOrder !== rightOrder) return leftOrder - rightOrder;

  const yDifference = finite(left?.position?.y) - finite(right?.position?.y);
  if (yDifference) return yDifference;
  const xDifference = finite(left?.position?.x) - finite(right?.position?.x);
  if (xDifference) return xDifference;
  return String(left?.id || '').localeCompare(String(right?.id || ''));
}

function escapeListText(value) {
  const singleLine = String(value || '').trim().replace(/\s+/g, ' ');
  if (!singleLine) return 'Untitled idea';
  return singleLine.replace(/^(\s*)([-+*>#]|\d+[.)])\s/, '$1\\$2 ');
}

function escapeLinkTitle(value) {
  return escapeListText(value)
    .replace(/\\/g, '\\\\')
    .replace(/\[/g, '\\[')
    .replace(/\]/g, '\\]');
}

function markdownTextForNode(node) {
  const rawText = String(node?.text || '').trim();
  const url = String(node?.linkUrl || (URL_ONLY.test(rawText) ? rawText : '')).trim();
  if (!url) return escapeListText(rawText);

  const title = rawText && !URL_ONLY.test(rawText)
    ? rawText
    : node?.linkMeta?.title || rawText || url;
  return `[${escapeLinkTitle(title)}](<${url.replace(/>/g, '%3E')}>)`;
}

function isExcludedAttachment(node) {
  return node?.attachment?.kind === 'image' || node?.attachment?.kind === 'markdown';
}

function markdownFilenameForNode(node) {
  const base = String(node?.text || node?.linkMeta?.title || 'branch-list')
    .trim()
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return `${base || 'branch-list'}.md`;
}

/**
 * Builds a stable Markdown snapshot and the graph mutation boundary for a
 * selected branch. Attachment ideas are exclusion frontiers: neither they nor
 * anything below them is converted in the first release.
 */
export function planBranchMarkdown({
  nodeId,
  nodes,
  relationships,
  actorUserId = null,
  rootId = null,
}) {
  const safeNodes = Array.isArray(nodes) ? nodes : [];
  const safeRelationships = Array.isArray(relationships) ? relationships : [];
  const nodesById = new Map(safeNodes.map((node) => [node.id, node]));
  const selectedNode = nodesById.get(nodeId);
  if (!selectedNode) throw new Error('The selected idea no longer exists.');
  if (isExcludedAttachment(selectedNode)) {
    throw new Error('Select a text or link idea to create a Markdown list.');
  }

  const childrenByParent = new Map();
  const parentRelationshipsByChild = new Map();
  safeRelationships.forEach((relationship) => {
    if (relationship?.childId) {
      const parents = parentRelationshipsByChild.get(relationship.childId) || [];
      parents.push(relationship);
      parentRelationshipsByChild.set(relationship.childId, parents);
    }
    const child = nodesById.get(relationship?.childId);
    if (!relationship?.parentId || !child) return;
    const children = childrenByParent.get(relationship.parentId) || [];
    children.push(child);
    childrenByParent.set(relationship.parentId, children);
  });
  childrenByParent.forEach((children) => children.sort(compareNodes));

  const eligibleNodes = [];
  const excludedRootIds = [];
  const excludedIds = new Set();
  const visited = new Set();

  const collectExcludedSubtree = (excludedNodeId) => {
    const pending = [excludedNodeId];
    let pendingIndex = 0;
    while (pendingIndex < pending.length) {
      const current = pending[pendingIndex];
      pendingIndex += 1;
      if (!current || excludedIds.has(current)) continue;
      excludedIds.add(current);
      pending.push(...(childrenByParent.get(current) || []).map((child) => child.id));
    }
  };

  const pending = [{ node: selectedNode, depth: 0 }];
  while (pending.length) {
    const { node, depth } = pending.pop();
    if (!node?.id || visited.has(node.id)) continue;
    visited.add(node.id);
    if (isExcludedAttachment(node)) {
      excludedRootIds.push(node.id);
      collectExcludedSubtree(node.id);
      continue;
    }
    eligibleNodes.push({ node, depth });
    const children = childrenByParent.get(node.id) || [];
    for (let childIndex = children.length - 1; childIndex >= 0; childIndex -= 1) {
      pending.push({ node: children[childIndex], depth: depth + 1 });
    }
  }

  const markdownLines = [];
  let markdownByteSize = 0;
  for (const { node, depth } of eligibleNodes) {
    const line = `${'  '.repeat(depth)}- ${markdownTextForNode(node)}`;
    markdownByteSize += utf8ByteLength(`${line}\n`);
    if (markdownByteSize > MAX_BRANCH_MARKDOWN_BYTES) {
      throw new Error('This branch is too large for a 2 MB Markdown file. Choose a smaller branch.');
    }
    markdownLines.push(line);
  }
  const markdown = `${markdownLines.join('\n')}\n`;
  const eligibleIds = eligibleNodes.map(({ node }) => node.id);
  const expectedEligibleNodes = eligibleNodes.map(({ node }) => (
    collaborativeContentSnapshot(node.id, node)
  ));
  const branchNodeIds = [...new Set([...eligibleIds, ...excludedIds])];
  const branchNodeIdSet = new Set(branchNodeIds);
  const danglingRelationship = safeRelationships.find((relationship) => (
    branchNodeIdSet.has(relationship.parentId) && !branchNodeIdSet.has(relationship.childId)
  ));
  if (danglingRelationship) {
    throw new Error('The branch contains a relationship to a missing idea.');
  }
  if (nodeId !== rootId && rootId && branchNodeIdSet.has(rootId)) {
    throw new Error('The branch contains a cycle through the main idea.');
  }
  branchNodeIds.forEach((branchNodeId) => {
    const parentRelationships = parentRelationshipsByChild.get(branchNodeId) || [];
    const expectedParentCount = branchNodeId === nodeId && nodeId === rootId ? 0 : 1;
    if (parentRelationships.length !== expectedParentCount) {
      throw new Error('The branch has an ambiguous parent relationship and cannot be converted safely.');
    }
  });
  const branchRelationships = safeRelationships
    .filter((relationship) => (
      branchNodeIdSet.has(relationship.parentId) || branchNodeIdSet.has(relationship.childId)
    ))
    .map((relationship) => ({
      parentId: relationship.parentId,
      childId: relationship.childId,
    }));
  const removedNodeIds = eligibleIds.filter((id) => id !== nodeId);
  const removedSet = new Set(removedNodeIds);
  const reparentedExclusions = excludedRootIds
    .map((excludedId) => {
      const relationship = (parentRelationshipsByChild.get(excludedId) || [])[0];
      if (!relationship || !removedSet.has(relationship.parentId)) return null;
      return {
        childId: excludedId,
        previousParentId: relationship.parentId,
        parentId: nodeId,
      };
    })
    .filter(Boolean);
  const foreignNode = actorUserId
    ? branchNodeIds
      .map((branchNodeId) => nodesById.get(branchNodeId))
      .find((node) => node?.userId && node.userId !== actorUserId)
    : null;
  const isRoot = nodeId === rootId || selectedNode.isRoot;

  return {
    nodeId,
    filename: markdownFilenameForNode(selectedNode),
    markdown,
    markdownByteSize,
    eligibleIds,
    expectedEligibleNodes,
    branchNodeIds,
    branchRelationships,
    removedNodeIds,
    excludedIds: [...excludedIds],
    reparentedExclusions,
    replaceAllowed: !isRoot && !foreignNode,
    replaceDisabledReason: isRoot
      ? 'The main idea must remain the root of the map. Keep the ideas instead.'
      : foreignNode
        ? 'Replace is unavailable because this branch contains an idea created by another collaborator.'
        : '',
  };
}

export function attachAssetToBranchReplacement(plan, asset, lastEditedBy = null) {
  if (!plan?.nodeId || !asset?.id || asset.kind !== 'markdown') {
    throw new Error('A planned branch and stored Markdown asset are required.');
  }
  if (!plan.replaceAllowed) {
    throw new Error(plan.replaceDisabledReason || 'This branch cannot be replaced.');
  }
  return {
    type: 'replace-branch-with-markdown',
    nodeId: plan.nodeId,
    eligibleIds: plan.eligibleIds,
    expectedEligibleNodes: plan.expectedEligibleNodes,
    branchNodeIds: plan.branchNodeIds,
    branchRelationships: plan.branchRelationships,
    removedNodeIds: plan.removedNodeIds,
    reparentedExclusions: plan.reparentedExclusions,
    nodePatch: {
      text: asset.filename,
      attachment: {
        assetId: asset.id,
        kind: asset.kind,
        filename: asset.filename,
        contentType: asset.contentType,
        byteSize: asset.byteSize,
        width: asset.width,
        height: asset.height,
      },
      linkUrl: null,
      linkMeta: null,
      embeddedMindmapId: null,
      isEmbeddedMindmapLink: false,
      isTask: false,
      taskDone: false,
      ...(lastEditedBy ? { lastEditedBy } : {}),
    },
  };
}

export async function replaceBranchWithStoredMarkdown({
  plan,
  upload,
  execute,
  cleanup,
  lastEditedBy = null,
}) {
  let asset = null;
  try {
    asset = await upload();
    const command = attachAssetToBranchReplacement(plan, asset, lastEditedBy);
    const nodeId = execute(command);
    if (!nodeId) throw new Error('The branch could not be replaced.');
    return { asset, command, nodeId };
  } catch (error) {
    if (asset) {
      try {
        await cleanup(asset);
      } catch (cleanupError) {
        if (error && typeof error === 'object') error.cleanupError = cleanupError;
      }
    }
    throw error;
  }
}
